import { useRef, useState, useCallback } from "react";
import { ZoomIn, ZoomOut, Maximize } from "lucide-react";

const MIN = 1;
const MAX = 5;
const clamp = (z) => Math.max(MIN, Math.min(MAX, +z.toFixed(3)));

export function DrawingViewer({ src, alt, maxHeight = "82vh", testid = "drawing-viewer" }) {
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef(null);
  const pointers = useRef(new Map());
  const panStart = useRef(null);
  const pinchStart = useRef(null);
  const [panning, setPanning] = useState(false);

  const zoomIn = () => setZoom((z) => clamp(z + 0.25));
  const zoomOut = () => setZoom((z) => clamp(z - 0.25));
  const reset = () => setZoom(1);

  const onPointerDown = useCallback((e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture?.(e.pointerId);
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchStart.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), zoom };
      panStart.current = null;
    } else if (pointers.current.size === 1 && zoom > 1) {
      const el = scrollRef.current;
      panStart.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
      setPanning(true);
    }
  }, [zoom]);

  const onPointerMove = useCallback((e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2 && pinchStart.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      setZoom(clamp(pinchStart.current.zoom * (dist / pinchStart.current.dist)));
      e.preventDefault();
    } else if (panStart.current) {
      const el = scrollRef.current;
      el.scrollLeft = panStart.current.left - (e.clientX - panStart.current.x);
      el.scrollTop = panStart.current.top - (e.clientY - panStart.current.y);
      e.preventDefault();
    }
  }, []);

  const onPointerUp = useCallback((e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
    if (pointers.current.size === 0) {
      panStart.current = null;
      setPanning(false);
    }
  }, []);

  const btn =
    "flex items-center justify-center w-8 h-8 bg-[#16181A]/90 border border-[#2E3033] text-[#A0AABF] hover:text-white hover:border-[#2560C9]/60 transition-colors disabled:opacity-40";

  return (
    <div ref={scrollRef} className="relative bg-[#0A0A0A] border border-[#2E3033] overflow-auto" style={{ maxHeight }}>
      <div className="sticky top-0 z-10 flex items-center gap-1.5 p-1.5 float-right">
        <button data-testid={`${testid}-zoom-out`} type="button" onClick={zoomOut} disabled={zoom <= MIN} className={btn} title="Zoom out">
          <ZoomOut className="w-4 h-4" />
        </button>
        <span data-testid={`${testid}-zoom-level`} className="flex items-center px-1.5 h-8 bg-[#16181A]/90 border border-[#2E3033] text-[10px] font-mono text-[#A0AABF]">
          {Math.round(zoom * 100)}%
        </span>
        <button data-testid={`${testid}-zoom-in`} type="button" onClick={zoomIn} disabled={zoom >= MAX} className={btn} title="Zoom in">
          <ZoomIn className="w-4 h-4" />
        </button>
        <button data-testid={`${testid}-zoom-reset`} type="button" onClick={reset} disabled={zoom === MIN} className={btn} title="Fit width">
          <Maximize className="w-4 h-4" />
        </button>
      </div>
      <img
        data-testid={testid}
        src={src}
        alt={alt}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={() => (zoom === 1 ? zoomIn() : reset())}
        className="h-auto bg-white select-none"
        style={{
          width: `${zoom * 100}%`,
          maxWidth: "none",
          touchAction: "none",
          cursor: zoom > 1 ? (panning ? "grabbing" : "grab") : "zoom-in",
        }}
        draggable={false}
      />
    </div>
  );
}
